import { apiFetch } from '../auth/apiClient'
import type {
  AddClientTodosRequest,
  AddClientTodosResponse,
  ClientTodoItem,
  TodosPayload,
} from '../contracts'

export const TODOS_UPDATED_EVENT = 'credem:todos-updated'

function redirectToLogin(): void {
  if (typeof window === 'undefined') return
  const next = encodeURIComponent(window.location.pathname + window.location.search)
  window.location.replace(`/login?next=${next}`)
}

async function handleErrorResponse(response: Response): Promise<never> {
  if (response.status === 401) redirectToLogin()
  let message = `Request failed with status ${response.status}`
  try {
    const payload = (await response.json()) as { error?: string; detail?: string }
    if (payload?.error) message = payload.error
    else if (payload?.detail) message = payload.detail
  } catch {
    // Keep the status-derived fallback.
  }
  throw new Error(message)
}

async function fetchJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await apiFetch(url, {
    headers: { Accept: 'application/json' },
    signal,
  })
  if (!response.ok) await handleErrorResponse(response)
  return (await response.json()) as T
}

async function sendJson<T>(
  method: 'POST' | 'PUT' | 'DELETE',
  url: string,
  body: Record<string, unknown> | null,
  signal?: AbortSignal,
): Promise<T> {
  const response = await apiFetch(url, {
    method,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: body == null ? undefined : JSON.stringify(body),
    signal,
  })
  if (!response.ok) await handleErrorResponse(response)
  return (await response.json()) as T
}

export function fetchTodos(signal?: AbortSignal): Promise<TodosPayload> {
  return fetchJson<TodosPayload>('/api/todos', signal)
}

export function addClientTodos(
  payload: AddClientTodosRequest,
  signal?: AbortSignal,
): Promise<AddClientTodosResponse> {
  return sendJson<AddClientTodosResponse>(
    'POST',
    '/api/todos/clients',
    payload as unknown as Record<string, unknown>,
    signal,
  )
}

export function completeTodo(
  todoId: string,
  note?: string,
): Promise<{ todo: ClientTodoItem }> {
  return sendJson<{ todo: ClientTodoItem }>(
    'POST',
    `/api/todos/${encodeURIComponent(todoId)}/complete`,
    note ? { note } : {},
  )
}

export function reorderTodos(activeTodoIds: string[]): Promise<{ ok: boolean }> {
  return sendJson<{ ok: boolean }>('PUT', '/api/todos/reorder', {
    active_todo_ids: activeTodoIds,
  })
}

export function markTodosSeen(todoIds: string[]): Promise<{ ok: boolean; marked: number }> {
  return sendJson<{ ok: boolean; marked: number }>('POST', '/api/todos/seen', {
    todo_ids: todoIds,
  }).then((result) => {
    window.dispatchEvent(new CustomEvent(TODOS_UPDATED_EVENT))
    return result
  })
}

export function deleteTodo(todoId: string): Promise<{ ok: boolean }> {
  return sendJson<{ ok: boolean }>(
    'DELETE',
    `/api/todos/${encodeURIComponent(todoId)}`,
    null,
  )
}
